/**
 * Password prompt for a statement MyDeck couldn't open (§7.3).
 *
 * Most issuers use a password you can derive once and save on the card. Some
 * rotate it every statement — a fresh one issued in their app each month —
 * which no saved password can ever match. For those, this is the only way in,
 * so it lives right in the review queue rather than behind a settings page.
 *
 * The file is already on disk, so unlocking never asks for a re-upload.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button, Input } from "@/components/ui/primitives";
import { statementsApi, type ReviewItem } from "@/lib/api/client";

export function UnlockPrompt({ item }: { item: ReviewItem }) {
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>();

  const filename = (item.payload?.filename as string) ?? "this statement";

  const unlock = useMutation({
    mutationFn: () => statementsApi.unlock(item.id, password),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["statements"] });
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      qc.invalidateQueries({ queryKey: ["cards"] });
      setPassword("");
      toast.success(r.message || "Statement unlocked.", {
        description: r.transaction_count
          ? `Read ${r.transaction_count} transactions.`
          : undefined,
      });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        setError(undefined);
        if (!password.trim()) {
          setError("Enter the password from your bank's app or email.");
          return;
        }
        unlock.mutate();
      }}
    >
      <div className="flex items-start gap-2">
        <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn" />
        <p className="text-xs text-warn">
          <span className="font-medium">{filename}</span> is locked and none of your saved
          passwords opened it. If this card issues a new password each statement, enter
          this month's below — the file is already saved, so there's nothing to re-upload.
        </p>
      </div>

      <div className="flex gap-2 pl-5">
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Statement password"
          autoComplete="off"
          className="h-8 max-w-xs text-xs"
          aria-label="Statement password"
        />
        <Button type="submit" variant="default" size="sm" disabled={unlock.isPending}>
          {unlock.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Unlock"}
        </Button>
      </div>

      {error && <p className="pl-5 text-xs text-overdue">{error}</p>}
    </form>
  );
}
